import React, { useEffect, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { organizationsService } from '../services/organizationsService';
import { UploadPollingProvider } from '../contexts/UploadPollingContext';
import { DownloadPollingProvider } from '../contexts/DownloadPollingContext';
import {
  Home,
  Braces,
  Settings,
  Menu,
  X,
  LogOut,
  User,
  Upload,
  Download,
  Search,
  MessageCircle,
  Activity,
  BarChart3,
  Layers,
  BookText,
  FileBarChart
} from 'lucide-react';
import VersionUpdater from './VersionUpdater';

function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [hasFastCubeAccess, setHasFastCubeAccess] = useState(false);
  const [hasOfferReportAccess, setHasOfferReportAccess] = useState(false);
  const { user, logout, passwordResetRequired } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const loadOrganizations = async () => {
      try {
        const orgs = await organizationsService.getOrganizations();
        if (!cancelled) {
          setHasFastCubeAccess(organizationsService.hasFastCubeAccess(orgs));
          setHasOfferReportAccess(organizationsService.hasOfferReportAccess(orgs));
        }
      } catch {
        if (!cancelled) {
          setHasFastCubeAccess(false);
          setHasOfferReportAccess(false);
        }
      }
    };
    loadOrganizations();
    return () => {
      cancelled = true;
    };
  }, []);

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    { name: 'Upload', href: '/file-upload', icon: Upload },
    { name: 'Segments', href: '/segments', icon: Layers },
    { name: 'API', href: '/api-keys', icon: Braces },
    { name: 'Email List Download', href: '/file-download', icon: Download },
    { name: 'Query', href: '/query', icon: Search },
    ...(hasFastCubeAccess ? [{ name: 'Chat', href: '/chat', icon: MessageCircle }] : []),
    { name: 'Signal File Request', href: '/signals', icon: Activity },
    ...(hasOfferReportAccess
      ? [{ name: 'Offer Reports', href: '/offer-report', icon: FileBarChart }]
      : []),
    ...(user?.is_admin ? [{ name: 'API Usage', href: '/api-usage', icon: BarChart3 }] : []),
    ...(user?.is_admin && hasFastCubeAccess
      ? [{ name: 'Data Dictionary', href: '/data-dictionary', icon: BookText }]
      : []),
    ...(user?.is_admin ? [{ name: 'Admin', href: '/admin', icon: Settings }] : []),
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <UploadPollingProvider>
    <DownloadPollingProvider>
    <div className="h-screen flex overflow-hidden bg-gray-100">
      <VersionUpdater />
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 flex z-40 md:hidden ${sidebarOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-6 w-6 text-white" />
            </button>
          </div>
          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <div className="flex-shrink-0 flex items-center px-4">
              <img src="/cube-logo.svg" alt="Cube Logo" className="h-8" />
            </div>
              <nav className="mt-5 px-2 space-y-1">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                const isDisabled = passwordResetRequired && item.href !== '/profile';
                return (
                  <Link
                    key={item.name}
                    to={isDisabled ? '/profile' : item.href}
                    onClick={(e) => {
                      if (isDisabled) {
                        e.preventDefault();
                        navigate('/profile');
                      }
                      setSidebarOpen(false);
                    }}
                    className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${
                      isActive
                        ? 'bg-primary-100 border-primary-500 text-primary-700'
                        : isDisabled
                        ? 'text-gray-400 cursor-not-allowed opacity-50'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <item.icon
                      className={`mr-4 h-6 w-6 ${
                        isActive ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                      }`}
                    />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col h-0 flex-1 border-r border-gray-200 bg-white">
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <div className="flex items-center flex-shrink-0 px-4">
                <img src="/cube-logo.svg" alt="Cube Logo" className="h-8" />
              </div>
              <nav className="mt-5 flex-1 px-2 bg-white space-y-1">
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  const isDisabled = passwordResetRequired && item.href !== '/profile';
                  return (
                    <Link
                      key={item.name}
                      to={isDisabled ? '/profile' : item.href}
                      onClick={(e) => {
                        if (isDisabled) {
                          e.preventDefault();
                          navigate('/profile');
                        }
                      }}
                      className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                        isActive
                          ? 'bg-primary-100 border-primary-500 text-primary-700'
                          : isDisabled
                          ? 'text-gray-400 cursor-not-allowed opacity-50'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <item.icon
                        className={`mr-3 h-5 w-5 ${
                          isActive ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                        }`}
                      />
                      {item.name}
                    </Link>
                  );
                })}
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        <div className="relative z-10 flex-shrink-0 flex h-16 bg-white shadow">
          <button
            type="button"
            className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 md:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex-1 px-4 flex justify-between">
            <div className="flex-1 flex">
              <h2 className="text-2xl font-semibold text-gray-900 my-auto">
                {navigation.find(item => item.href === location.pathname)?.name || (location.pathname === '/profile' ? 'Profile' : 'Dashboard')}
              </h2>
            </div>
            <div className="ml-4 flex items-center md:ml-6">
              <div className="flex items-center space-x-4">
                <Link
                  to="/profile"
                  className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 px-2 py-1.5 rounded-md hover:bg-gray-50 transition-colors"
                >
                  <User className="h-5 w-5 text-gray-400" />
                  <span className="text-sm font-medium">
                    {user?.first_name} {user?.last_name}
                  </span>
                  {user?.is_admin && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                      Admin
                    </span>
                  )}
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 text-gray-500 hover:text-gray-700 px-3 py-2 rounded-md text-sm font-medium"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div
              className={`mx-auto px-4 sm:px-6 md:px-8 ${
                location.pathname.startsWith('/segments')
                  ? 'max-w-none'
                  : location.pathname.startsWith('/admin')
                    ? 'max-w-[100rem]'
                    : 'max-w-7xl'
              }`}
            >
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
    </DownloadPollingProvider>
    </UploadPollingProvider>
  );
}

export default Layout;
